import styled, { keyframes } from 'styled-components';
import { C, S } from '../tokens';

// ─── Animations ────────────────────────────────────────────────────────────
export const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(5px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const slideIn = keyframes`
  from { opacity: 0; transform: translateX(10px); }
  to   { opacity: 1; transform: translateX(0); }
`;

// ─── Stat Cards ────────────────────────────────────────────────────────────
export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 20px;
  animation: ${fadeUp} 0.24s ease both;
`;

export const StatCard = styled.div`
  background: ${C.sf};
  border: 1px solid ${C.bd};
  border-radius: 11px;
  padding: 15px 17px;
  box-shadow: ${S.sh1};
`;

export const StatLabel = styled.div`
  font-size: 10.5px;
  color: ${C.ink3};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 5px;
`;

export const StatValue = styled.div`
  font-family: 'Syne', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: ${C.ink};
  line-height: 1;
`;

export const StatValueSm = styled(StatValue)`
  font-size: 16px;
`;

export const StatValueMono = styled(StatValue)`
  font-family: monospace;
  font-size: 14px;
  color: ${C.or};
`;

export const StatSub = styled.div`
  font-size: 11px;
  color: ${C.ink4};
  margin-top: 3px;
`;

// ─── Tag — function component to avoid non-DOM prop warnings in SC v4 ──────
const TAG_VARIANTS = {
  gr: { background: C.grLt, color: C.gr },
  rd: { background: C.rdLt, color: C.rd },
  am: { background: C.amLt, color: C.am },
  bl: { background: C.blLt, color: C.bl },
  or: { background: C.orLt, color: C.or },
  gy: { background: C.sf3,  color: C.ink3 },
  te: { background: C.teLt, color: C.te },
};

const TAG_BASE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '3px',
  padding: '2px 9px',
  borderRadius: '99px',
  fontSize: '11px',
  fontWeight: '600',
  letterSpacing: '0.2px',
};

export const Tag = ({ variant = 'gy', children, style, ...rest }) => (
  <span style={{ ...TAG_BASE, ...TAG_VARIANTS[variant], ...style }} {...rest}>
    {children}
  </span>
);

// ─── Download Button ────────────────────────────────────────────────────────
export const DownloadButtonPdf = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 10px 18px;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: 'DM Sans', sans-serif;
  transition: all 0.14s;
  background: ${C.rdLt};
  color: ${C.rd};
  border: 1px solid ${C.rdBd};

  &:hover { transform: translateY(-1px); box-shadow: ${S.sh2}; }
`;

export const DownloadButtonCsv = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 10px 18px;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: 'DM Sans', sans-serif;
  transition: all 0.14s;
  background: ${C.blLt};
  color: ${C.bl};
  border: 1px solid ${C.blBd};

  &:hover { transform: translateY(-1px); box-shadow: ${S.sh2}; }
`;

// ─── Notice Box ─────────────────────────────────────────────────────────────
export const NoticeBox = styled.div`
  padding: 12px 16px;
  background: ${C.sf3};
  border: 1px solid ${C.bd};
  border-radius: 9px;
  font-size: 12.5px;
  color: ${C.ink3};
  line-height: 1.65;
  margin-bottom: 20px;
  animation: ${fadeUp} 0.24s ease both;
`;

// ─── Earnings Card ──────────────────────────────────────────────────────────
export const EarnCard = styled.div`
  background: ${C.sf};
  border: 1px solid ${C.bd};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: ${S.sh1};
  margin-bottom: 20px;
  animation: ${fadeUp} 0.24s ease both;
`;

export const EarnCardHeader = styled.div`
  padding: 13px 18px;
  border-bottom: 1px solid ${C.bd};
  font-family: 'Syne', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: ${C.ink};
  display: flex;
  align-items: center;
  justify-content: space-between;

  > span:last-child {
    font-size: 12px;
    color: ${C.ink3};
    font-family: 'DM Sans', sans-serif;
    font-weight: 400;
  }
`;

export const EarnRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 11px 18px;
  border-bottom: 1px solid ${C.sf3};
  background: ${({ total }) => total ? C.sf2 : 'transparent'};

  &:last-child { border-bottom: none; }
`;

export const EarnLabel = styled.span`
  font-size: ${({ total }) => total ? '14px' : '13.5px'};
  color: ${C.ink};
  font-weight: ${({ total }) => total ? '600' : '400'};
`;

export const EarnValue = styled.span`
  font-size: ${({ total }) => total ? '15px' : '13.5px'};
  font-weight: ${({ total }) => total ? '800' : '700'};
  color: ${C.ink};
`;

// ─── Generic Button ─────────────────────────────────────────────────────────
export const BtnPrimary = styled.button`
  background: ${C.or};
  color: #fff;
  padding: 5px 11px;
  font-size: 12px;
  border-radius: 7px;
  border: none;
  cursor: pointer;
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  transition: all 0.14s;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover { opacity: 0.86; transform: translateY(-1px); }
  &:active { transform: translateY(0); }
`;

export const BtnSecondary = styled.button`
  background: ${C.sf3};
  color: ${C.ink2};
  border: 1px solid ${C.bd};
  padding: 5px 11px;
  font-size: 12px;
  border-radius: 7px;
  cursor: pointer;
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  transition: all 0.14s;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover { opacity: 0.86; transform: translateY(-1px); }
  &:active { transform: translateY(0); }
`;
